// Single source of truth for the receipt sizing settings (/outlet_form > Settings):
// the thermal paper width and the logo widths for each print family.
//
// These defaults ARE the values the receipt templates fall back to, so the outlet form
// and the templates can never drift apart.

// Thermal roll width (mm). 58 and 80 are the two standard rolls; the range also covers
// the narrow 48 mm and wide 112 mm rolls seen in the field.
export const RECEIPT_WIDTH = { min: 40, max: 120, def: 58 };

// Logo width (px). Thermal rolls and A4/A5 pages have very different usable widths,
// so each print family gets its own bounds and default.
export const LOGO_WIDTH = {
  thermal: { min: 40, max: 200, def: 80 }, // thermal roll
  a4: { min: 40, max: 300, def: 160 }, // A4 / A5 page
};

/**
 * Resolve a stored/typed width into a value that is always safe to render.
 *
 * - blank, non-numeric, 0 or negative -> the template default (never a 0-wide element)
 * - anything else -> clamped into [min, max]
 *
 * @param {*} value raw value from the form or the outlet record
 * @param {{min: number, max: number, def: number}} bounds
 * @returns {number}
 */
export const resolveWidth = (value, { min, max, def }) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return def;
  return Math.min(Math.max(n, min), max);
};

/** Thermal paper width in mm, safe to render. */
export const resolveReceiptWidth = (value) => resolveWidth(value, RECEIPT_WIDTH);

// Pre-printed letterhead: how much of the sheet is already occupied by the printed
// header and footer, in mm, so the A4/A5 page rule can reserve it. Sun Moon's stock
// measures ~42 mm of header and ~53 mm of footer; the defaults add a couple of mm of
// clearance. 120 is half an A4 sheet — past that there is no room left to print an
// invoice, so it is a sane ceiling rather than a real-world value.
export const LETTERHEAD_MM = {
  top: { min: 0, max: 120, def: 45 },
  bottom: { min: 0, max: 120, def: 55 },
};

/**
 * Reserved letterhead band in mm, safe to render.
 *
 * Deliberately NOT resolveWidth: that treats 0 as "unset" and substitutes the default,
 * which is right for a width (a 0-wide logo is a bug) and wrong for a margin. A
 * letterhead with a printed header and no printed footer legitimately reserves 0 mm at
 * the bottom, so only a blank/non-numeric value falls back to the default here.
 *
 * @param {*} value raw value from the form or the outlet record
 * @param {"top"|"bottom"} edge which band
 * @returns {number}
 */
export const resolveLetterheadMm = (value, edge = "top") => {
  const { min, max, def } = LETTERHEAD_MM[edge] || LETTERHEAD_MM.top;
  const n = Number(value);
  if (!Number.isFinite(n)) return def;
  return Math.min(Math.max(n, min), max);
};

/**
 * Logo width in px, safe to render.
 * @param {"thermal"|"a4"} kind print family
 */
export const resolveLogoWidth = (value, kind = "thermal") =>
  resolveWidth(value, LOGO_WIDTH[kind] || LOGO_WIDTH.thermal);
